import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  SvMessageComponent,
  SvMessageConfig,
  SvMessageResult,
  SvMessageType,
} from '../components/sv-message/sv-message.component';

/**
 * SvMessageService — Serviço nativo de mensagens para substituir SweetAlert2.
 *
 * Fornece a mesma API que o AlertService e AlertMessageService existentes,
 * mas usa um componente Angular nativo em vez do SweetAlert2.
 *
 * UTILIZAÇÃO:
 *
 * // Injetar no componente
 * constructor(private svMessage: SvMessageService) {}
 *
 * // Mensagens simples
 * this.svMessage.success('Guardado com sucesso!');
 * this.svMessage.error('Não foi possível processar.');
 * this.svMessage.warning('Dados incompletos.');
 * this.svMessage.info('Operação em processamento.');
 *
 * // Mensagem com título e texto separados (equivalente a Swal.fire('titulo','texto','tipo'))
 * this.svMessage.fire('Sucesso', 'Dados guardados.', 'success');
 *
 * // Confirmação (equivalente a Swal.fire com showCancelButton: true)
 * const confirmed = await this.svMessage.confirm('Tem a certeza?');
 * if (confirmed) { ... }
 *
 * // Toast rápido
 * this.svMessage.toast('Guardado!', 'success');
 *
 * // Configuração avançada
 * const result = await this.svMessage.fire({
 *   type: 'warning',
 *   title: 'Atenção',
 *   text: 'Deseja continuar?',
 *   showCancelButton: true,
 *   confirmButtonText: 'Sim',
 *   cancelButtonText: 'Não',
 * });
 */
@Injectable({ providedIn: 'root' })
export class SvMessageService {
  /** Referência estática para o wrapper de compatibilidade Swal */
  static instance: SvMessageService | null = null;

  private componentRef: ComponentRef<SvMessageComponent> | null = null;
  private appRef = inject(ApplicationRef);
  private injector = inject(EnvironmentInjector);
  private translate = inject(TranslateService);

  constructor() {
    SvMessageService.instance = this;
  }

  /**
   * Obtém (ou cria) a instância do componente de mensagens
   */
  private getComponent(): SvMessageComponent {
    if (!this.componentRef) {
      this.componentRef = createComponent(SvMessageComponent, {
        environmentInjector: this.injector,
      });
      this.appRef.attachView(this.componentRef.hostView);
      document.body.appendChild(this.componentRef.location.nativeElement);
    }
    return this.componentRef.instance;
  }

  // ────────────────────────────────────────────────────
  //  API principal — fire()
  // ────────────────────────────────────────────────────

  /**
   * Método genérico fire() — substituto direto de Swal.fire().
   *
   * Aceita 3 assinaturas:
   *  1. fire(config: SvMessageConfig)
   *  2. fire(title, text, type)
   *  3. fire(title, type) — sem texto
   */
  fire(
    titleOrConfig: string | Partial<SvMessageConfig>,
    text?: string,
    type?: SvMessageType,
  ): Promise<SvMessageResult> {
    let config: SvMessageConfig;

    if (typeof titleOrConfig === 'string') {
      config = {
        type: type || 'info',
        title: titleOrConfig,
        text: text,
      };
    } else {
      config = {
        type: titleOrConfig.type || 'info',
        ...titleOrConfig,
      } as SvMessageConfig;
    }

    if (config.type === 'error') {
      console.group(
        '%c🚨 [ERRO NO SITE DETETADO]',
        'background: #dc3545; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
      );
      console.error('Título:', config.title);
      if (config.text) console.error('Mensagem:', config.text);
      if (config.html) console.error('HTML:', config.html);
      console.trace('Origem do erro (Call Stack):');
      console.groupEnd();
    } else if (config.type === 'warning') {
      console.groupCollapsed(
        '%c⚠️ [AVISO NO SITE]',
        'background: #ffc107; color: #000; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
      );
      console.warn('Título:', config.title);
      if (config.text) console.warn('Mensagem:', config.text);
      console.trace('Origem do aviso (Call Stack):');
      console.groupEnd();
    }

    return new Promise<SvMessageResult>(resolve => {
      config.resolve = resolve;
      this.getComponent().show(config);
    });
  }

  // ────────────────────────────────────────────────────
  //  Atalhos por tipo
  // ────────────────────────────────────────────────────

  /**
   * Mensagem de sucesso
   */
  success(message: string, title?: string, timer?: number): Promise<SvMessageResult> {
    return this.fire({
      type: 'success',
      title: title || this.tr('COMMON.ALERTS.SUCCESS'),
      text: this.tr(message),
      confirmButtonText: this.tr('ACTIONS.OK'),
      timer: timer,
      showConfirmButton: !timer,
    });
  }

  /**
   * Mensagem de conclusão / sucesso (alias de conveniência)
   */
  done(message: string, title?: string, timer?: number): Promise<SvMessageResult> {
    return this.success(message, title, timer);
  }

  /**
   * Mensagem de erro
   */
  error(message: string, title?: string): Promise<SvMessageResult> {
    return this.fire({
      type: 'error',
      title: title || 'Erro',
      text: this.tr(message),
      confirmButtonText: this.tr('ACTIONS.OK'),
    });
  }

  /**
   * Mensagem de aviso
   */
  warning(message: string, title?: string): Promise<SvMessageResult> {
    return this.fire({
      type: 'warning',
      title: title || this.tr('COMMON.ALERTS.ATTENTION'),
      text: this.tr(message),
      confirmButtonText: this.tr('ACTIONS.OK'),
    });
  }

  /**
   * Mensagem informativa
   */
  info(message: string, title?: string): Promise<SvMessageResult> {
    return this.fire({
      type: 'info',
      title: title || 'Informação',
      text: this.tr(message),
      confirmButtonText: this.tr('ACTIONS.OK'),
    });
  }

  /**
   * Toast rápido (notificação não intrusiva)
   */
  toast(message: string, type: SvMessageType = 'success', timer: number = 3000): Promise<SvMessageResult> {
    return this.fire({
      type,
      title: this.tr(message),
      toast: true,
      timer,
      showConfirmButton: false,
    });
  }

  // ────────────────────────────────────────────────────
  //  Confirmação
  // ────────────────────────────────────────────────────

  /**
   * Diálogo de confirmação — retorna true se confirmado, false se cancelado
   */
  async confirm(message: string, title?: string): Promise<boolean> {
    const result = await this.fire({
      type: 'question',
      title: title || 'Confirmar',
      text: this.tr(message),
      showCancelButton: true,
      confirmButtonText: this.tr('ACTIONS.YES') || 'Sim',
      cancelButtonText: this.tr('ACTIONS.NO') || 'Não',
      reverseButtons: true,
    });
    return result.isConfirmed;
  }

  /**
   * Confirmação de eliminação
   */
  async confirmDelete(itemName?: string): Promise<boolean> {
    const message = itemName
      ? `Tem a certeza que deseja eliminar "${itemName}"?`
      : 'Tem a certeza que deseja eliminar este item?';

    const result = await this.fire({
      type: 'warning',
      title: 'Confirmar Eliminação',
      text: message,
      showCancelButton: true,
      confirmButtonText: this.tr('ACTIONS.DELETE') || 'Eliminar',
      cancelButtonText: this.tr('COMMON.BUTTONS.CANCEL') || 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
    });
    return result.isConfirmed;
  }
  // ────────────────────────────────────────────────────
  //  Métodos especializados (compatibilidade AlertService)
  // ────────────────────────────────────────────────────

  showNetworkError(): Promise<SvMessageResult> {
    return this.error(
      'Não foi possível contactar o servidor. Verifique a sua ligação à internet e tente novamente.',
      'Erro de Comunicação',
    );
  }

  showValidationError(message?: string): Promise<SvMessageResult> {
    return this.warning(message || 'Por favor, verifique os dados inseridos e tente novamente.', 'Dados Inválidos');
  }

  showUnauthorizedError(): Promise<SvMessageResult> {
    return this.error('Não tem permissão para realizar esta operação.', 'Acesso Negado');
  }

  showSessionExpired(): Promise<SvMessageResult> {
    return this.warning('A sua sessão expirou. Por favor, inicie sessão novamente.', 'Sessão Expirada');
  }

  showCustomHtml(html: string, title?: string, type: SvMessageType = 'info'): Promise<SvMessageResult> {
    return this.fire({
      type,
      title,
      html,
    });
  }

  /** mapear erros HTTP comuns para mensagens traduzidas */
  fromHttpError(status?: number, fallbackKey: string = 'ERRORS.UNEXPECTED'): Promise<SvMessageResult> {
    switch (status) {
      case 0:
        return this.showNetworkError();
      case 400:
        return this.error(this.tr('ERRORS.BAD_REQUEST'));
      case 401:
        return this.showUnauthorizedError();
      case 403:
        return this.error(this.tr('ERRORS.FORBIDDEN'));
      case 404:
        return this.error(this.tr('ERRORS.NOT_FOUND'));
      case 408:
        return this.error(this.tr('ERRORS.TIMEOUT'));
      case 413:
        return this.error(this.tr('ERRORS.PAYLOAD_TOO_LARGE'));
      case 503:
        return this.error(this.tr('ERRORS.UNAVAILABLE'));
      default:
        return this.error(this.tr(fallbackKey));
    }
  }

  // ────────────────────────────────────────────────────
  //  Utilitários internos
  // ────────────────────────────────────────────────────

  /**
   * Traduz uma chave. Se a chave não existir nas traduções, devolve o texto tal como está.
   * Evita que o ngx-translate adicione parênteses retos ao texto.
   */
  private tr(key: string, params?: Record<string, any>): string {
    if (!key) return '';
    const result = this.translate.instant(key, params);
    // ngx-translate devolve [chave] quando não encontra a tradução
    if (result === `[${key}]`) return key;
    // Se translate.instant() devolveu algo diferente da chave, a tradução existe
    if (result !== key) return result;
    // Caso contrário, devolve o texto original (é texto literal, não uma chave)
    return key;
  }
}
